import axios from "axios";

/* -------------------------------------------------------------------
 * Backend URL resolution
 *
 * `REACT_APP_BACKEND_URL` is a platform-managed secret on Emergent and
 * is auto-populated with the custom domain (e.g. https://innercompass.tech).
 * That value is NOT a backend host — it serves only the static frontend.
 * We therefore prefer an app-owned secret (`REACT_APP_API_URL`), which
 * points at the real Inner Compass backend used by the mobile app
 * (https://emotional-compass-27.emergent.host). Fallback to the legacy
 * secret only if `REACT_APP_API_URL` is unset.
 * ----------------------------------------------------------------- */
function resolveBackendUrl() {
  const custom = (process.env.REACT_APP_API_URL || "").trim();
  const legacy = (process.env.REACT_APP_BACKEND_URL || "").trim();
  const chosen = custom || legacy;
  if (chosen && typeof window !== "undefined") {
    // Guard: never let the frontend talk to its own origin as if it were the backend.
    try {
      const origin = window.location.origin;
      if (chosen.replace(/\/$/, "") === origin.replace(/\/$/, "")) {
        // eslint-disable-next-line no-console
        console.warn("[InnerCompass] Backend URL equals the frontend origin; API calls would self-route. Check REACT_APP_API_URL.");
      }
    } catch {}
  }
  return chosen.replace(/\/$/, "");
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

/* B2B service (Stripe seat billing, occupational health, consent centre, audit).
 * Defaults to the same origin that serves this web app (its /api is proxied to the
 * B2B FastAPI service). Set REACT_APP_B2B_API_URL when the B2B routers are mounted
 * on the main Inner Compass backend instead. */
const B2B_URL = ((process.env.REACT_APP_B2B_API_URL || "").trim() || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
export const B2B_API_BASE = `${B2B_URL}/api`;

const TOKEN_KEY = "ic_token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

export const setToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

export const clearToken = () => setToken("");

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

export const b2bApi = axios.create({
  baseURL: B2B_API_BASE,
  headers: { "Content-Type": "application/json" },
});

b2bApi.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Global 401 handler
let onUnauthorized = null;
export const setOnUnauthorized = (fn) => {
  onUnauthorized = fn;
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && onUnauthorized) {
      onUnauthorized(err);
    }
    return Promise.reject(err);
  }
);

/** Extract a token from an auth response — the backend may return several shapes. */
export const pickToken = (data) =>
  data?.token ||
  data?.access_token ||
  data?.bearer ||
  data?.session_token ||
  data?.jwt ||
  "";

/** Extract a friendly error string from an axios error. */
export const errMsg = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map((x) => x.msg || x).join(", ");
  return d.message || d.error || fallback;
};
